import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

export default function EcommerceSolutions() {
  const features = [
    {
      icon: 'ri-shopping-cart-line',
      title: 'Complete Store Setup',
      description: 'Full e-commerce platform with product catalog, shopping cart, and checkout.'
    },
    {
      icon: 'ri-secure-payment-line',
      title: 'Secure Payments',
      description: 'Integrated payment processing with Stripe, PayPal, and major credit cards.'
    },
    {
      icon: 'ri-box-3-line',
      title: 'Inventory Management',
      description: 'Track stock levels, manage variants, and automate low-stock alerts.'
    },
    {
      icon: 'ri-truck-line',
      title: 'Shipping Integration',
      description: 'Real-time shipping rates and tracking with major carriers.'
    },
    {
      icon: 'ri-user-line',
      title: 'Customer Accounts',
      description: 'User registration, order history, and personalized experiences.'
    },
    {
      icon: 'ri-bar-chart-line',
      title: 'Sales Analytics',
      description: 'Detailed reports on sales, customers, and product performance.'
    }
  ];

  const platforms = [
    {
      name: 'Shopify',
      description: 'Perfect for most businesses',
      features: ['Easy to use', 'App ecosystem', 'Built-in payments', 'Mobile optimized'],
      price: 'From $1,499'
    },
    {
      name: 'WooCommerce',
      description: 'WordPress-based solution',
      features: ['Full customization', 'SEO friendly', 'Plugin ecosystem', 'Cost effective'],
      price: 'From $1,299'
    },
    {
      name: 'Custom Solution',
      description: 'Tailored to your needs',
      features: ['Unique features', 'Full control', 'Scalable', 'API integrations'],
      price: 'From $2,999'
    }
  ];

  const addOns = [
    {
      title: 'Product Photography',
      description: 'Professional product photos that sell',
      price: '$99/product'
    },
    {
      title: 'Product Descriptions',
      description: 'SEO-optimized copy that converts',
      price: '$49/product'
    },
    {
      title: 'Email Marketing Setup',
      description: 'Automated email sequences for sales',
      price: '$299'
    },
    {
      title: 'Social Media Integration',
      description: 'Sell on Facebook, Instagram, and more',
      price: '$199'
    },
    {
      title: 'Advanced Analytics',
      description: 'Google Analytics 4 and conversion tracking',
      price: '$149'
    },
    {
      title: 'Multi-language Support',
      description: 'Reach global customers',
      price: '$499'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="py-20 bg-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
                E-commerce
                <span className="block text-emerald-700">
                  Solutions
                </span>
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Launch your online store with a complete e-commerce solution. From product catalog 
                to secure checkout, we build everything you need to start selling online.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/vibe-check"
                  className="bg-green-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-green-700 transition-colors whitespace-nowrap text-center"
                >
                  Start Your Store
                </Link>
                <Link
                  to="/contact"
                  className="border-2 border-green-600 text-green-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-green-600 hover:text-white transition-colors whitespace-nowrap text-center"
                >
                  Get Free Quote
                </Link>
              </div>
            </div>
            <div className="relative">
              <img
                src="/site-images/e-commerce-hero.webp"
                alt="E-commerce Solutions"
                className="w-full h-auto rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Complete E-commerce Features</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to run a successful online business, from inventory management to customer analytics.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1">
                <div className="w-16 h-16 bg-green-100 rounded-lg flex items-center justify-center mb-6">
                  <i className={`${feature.icon} text-green-600 text-2xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Platforms Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Choose Your Platform</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We work with the best e-commerce platforms to match your specific needs and budget.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {platforms.map((platform, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{platform.name}</h3>
                <p className="text-gray-600 mb-6">{platform.description}</p>
                <ul className="space-y-3 mb-8">
                  {platform.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-600">
                      <i className="ri-check-line text-green-500 mr-3"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
                <div className="text-2xl font-bold text-green-600 mb-6">{platform.price}</div>
                <Link
                  to="/vibe-check"
                  className="w-full block text-center bg-green-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-green-700 transition-colors whitespace-nowrap"
                >
                  Choose Platform
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Add-ons Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Boost Your Sales</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Additional services to help your online store succeed and grow.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {addOns.map((addon, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{addon.title}</h3>
                <p className="text-gray-600 mb-4">{addon.description}</p>
                <div className="flex justify-between items-center">
                  <span className="text-2xl font-bold text-green-600">{addon.price}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Success Stories</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See how our e-commerce solutions have helped businesses grow their online sales.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-lg text-center">
              <div className="text-4xl font-bold text-green-600 mb-2">300%</div>
              <div className="text-gray-600">Average sales increase in first 6 months</div>
            </div>
            <div className="bg-white rounded-xl p-8 shadow-lg text-center">
              <div className="text-4xl font-bold text-green-600 mb-2">24hrs</div>
              <div className="text-gray-600">Average time to launch your store</div>
            </div>
            <div className="bg-white rounded-xl p-8 shadow-lg text-center">
              <div className="text-4xl font-bold text-green-600 mb-2">99.9%</div>
              <div className="text-gray-600">Uptime guarantee for your store</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-emerald-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Ready to Start Selling Online?</h2>
          <p className="text-xl text-emerald-100 mb-8">
            Launch your e-commerce store and start generating revenue within days, not months.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/vibe-check"
              className="bg-white text-green-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap"
            >
              Start Your Store
            </Link>
            <Link
              to="/portfolio"
              className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-green-600 transition-colors whitespace-nowrap"
            >
              View Store Examples
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
